import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../api';
import BaseLayout from './BaseLayout';
import FlameLoader from './FlameLoader';
import { useAuth } from '../context/AuthContext';

const SPORTS = ['NBA', 'EPL', 'MLS', 'MLB'];

const JOIN_POLICY_LABELS = {
  open:        'Open',
  request:     'Request to Join',
  invite_only: 'Invite Only',
};



function GroupAvatar({ name, avatarUrl, size = 48 }) {
  const [err, setErr] = useState(false);
  const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  if (avatarUrl && !err) {
    return (
      <img src={avatarUrl} alt={name} className="grp-avatar grp-avatar--img"
        style={{ width: size, height: size }} onError={() => setErr(true)} />
    );
  }
  return (
    <div className="grp-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

function MemberAvatar({ name, picture, size = 36 }) {
  return (
    <img
      src={picture || '/default.jpg'}
      alt={name || '?'}
      className="grp-member-avatar grp-member-avatar--img"
      style={{ width: size, height: size }}
      onError={e => { e.target.src = '/default.jpg'; }}
    />
  );
}

export default function GroupPage() {
  const { groupId } = useParams();
  const { authData } = useAuth();
  const navigate     = useNavigate();
  const token        = authData?.token;
  const currentUser  = authData?.user;
  const headers      = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [group,        setGroup]        = useState(null);
  const [leaderboard,  setLeaderboard]  = useState([]);
  const [myRole,       setMyRole]       = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');

  const [activeTab, setActiveTab] = useState('leaderboard');

  // Settings / edit form
  const [editForm,    setEditForm]    = useState({});
  const [saving,      setSaving]      = useState(false);
  const [saveMsg,     setSaveMsg]     = useState('');
  const [saveErr,     setSaveErr]     = useState('');
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [deleting,    setDeleting]    = useState(false);

  // Invite search
  const [inviteQ,       setInviteQ]       = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteSent,    setInviteSent]    = useState({}); // userId → msg
  const inviteTimer = useRef(null);

  // Invite link
  const [inviteLink, setInviteLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);

  // Join feedback
  const [joinFeedback, setJoinFeedback] = useState('');
  const [joining, setJoining] = useState(false);

  // ── Game picker state ────────────────────────────────────────────────────
  const [availGames,    setAvailGames]    = useState([]);
  const [availLoading,  setAvailLoading]  = useState(false);
  const [availPage,     setAvailPage]     = useState(1);
  const [availHasNext,  setAvailHasNext]  = useState(false);
  const [gameQ,         setGameQ]         = useState('');
  const [gameSport,     setGameSport]     = useState('');
  const [selectedIds,   setSelectedIds]   = useState(new Set());
  const [gameMsg,       setGameMsg]       = useState('');
  const gameTimer = useRef(null);

  // ── Group games schedule (all members) ──────────────────────────────────
  const [groupGames,        setGroupGames]        = useState([]);
  const [groupGamesLoading, setGroupGamesLoading] = useState(false);

  // ── Preset state ─────────────────────────────────────────────────────────
  const [presetLoading, setPresetLoading] = useState(false);
  const [presetMsg,     setPresetMsg]     = useState('');
  const [presetErr,     setPresetErr]     = useState('');
  const [presetTeamQ,   setPresetTeamQ]   = useState('');

  // ── Formula builder state ────────────────────────────────────────────────
  const [formula,       setFormula]       = useState('');
  const [formulaMsg,    setFormulaMsg]    = useState('');
  const [formulaErr,    setFormulaErr]    = useState('');
  const [formulaSaving, setFormulaSaving] = useState(false);

  // ── Leaderboard UI state ─────────────────────────────────────────────────
  const [lbRefreshing, setLbRefreshing] = useState(false);
  const [lbInfoOpen,   setLbInfoOpen]   = useState(false);

  // ── Notification prefs ─────────────────────────────────────────────────
  const [myNotify,      setMyNotify]      = useState(true);
  const [notifySaving,  setNotifySaving]  = useState(false);

  // ── Avatar upload ───────────────────────────────────────────────────────
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarErr,       setAvatarErr]       = useState('');
  const avatarInputRef = useRef(null);

  // ── Fetch group data ─────────────────────────────────────────────────────
  const fetchGroup = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/groups/${groupId}`, { headers });
      const d   = res.data;
      setGroup(d.group);
      setLeaderboard(d.leaderboard || []);
      setMyRole(d.my_role);
      setJoinRequests(d.join_requests || []);
      setFormula(d.group.score_formula || '');
      setMyNotify(d.my_notify ?? true);
      setEditForm({
        name:         d.group.name,
        description:  d.group.description || '',
        is_public:    d.group.is_public,
        join_policy:  d.group.join_policy,
        max_members:  d.group.max_members,
        sport_filter: d.group.sport_filter || '',
      });
      if (d.group.invite_token) {
        setInviteLink(`${window.location.origin}/groups/join/${d.group.invite_token}`);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load group');
    } finally {
      setLoading(false);
    }
  }, [groupId, headers]);

  // ── Lightweight leaderboard refresh (no full-page spinner) ───────────────
  const refreshLeaderboard = useCallback(async () => {
    setLbRefreshing(true);
    try {
      const res = await axios.get(`${API_BASE}/groups/${groupId}`, { headers });
      setGroup(res.data.group);
      setLeaderboard(res.data.leaderboard || []);
    } catch { }
    finally { setLbRefreshing(false); }
  }, [groupId, headers]);

  // ── Notification preference handlers ──────────────────────────────────
  const handleToggleOwnerNotify = async (val) => {
    setNotifySaving(true);
    try {
      await axios.put(`${API_BASE}/groups/${groupId}/owner-notifications`,
        { notify_owner_join_leave: val }, { headers });
      setGroup(prev => ({ ...prev, notify_owner_join_leave: val }));
    } catch { }
    finally { setNotifySaving(false); }
  };

  const handleToggleMyNotify = async (val) => {
    setNotifySaving(true);
    try {
      await axios.put(`${API_BASE}/groups/${groupId}/my-notifications`,
        { notify_member: val }, { headers });
      setMyNotify(val);
    } catch { }
    finally { setNotifySaving(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarErr('');
    setAvatarUploading(true);
    const formData = new FormData();
    formData.append('avatar', file);
    try {
      const res = await axios.post(
        `${API_BASE}/groups/${groupId}/avatar`,
        formData,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      setGroup(prev => ({ ...prev, avatar_url: res.data.avatar_url }));
    } catch (err) {
      setAvatarErr(err.response?.data?.error || 'Upload failed');
    } finally {
      setAvatarUploading(false);
      e.target.value = '';
    }
  };

  useEffect(() => {
    document.body.classList.add('account-page-bg');
    fetchGroup();
    return () => document.body.classList.remove('account-page-bg');
  }, [fetchGroup]);

  // ── Join group ───────────────────────────────────────────────────────────
  const handleJoin = async () => {
    setJoining(true);
    setJoinFeedback('');
    try {
      const res = await axios.post(`${API_BASE}/groups/${groupId}/join`, {}, { headers });
      setJoinFeedback(res.data.message);
      fetchGroup();
    } catch (e) {
      setJoinFeedback(e.response?.data?.error || 'Failed to join');
    } finally {
      setJoining(false);
    }
  };

  // ── Leave group ──────────────────────────────────────────────────────────
  const handleLeave = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await axios.post(`${API_BASE}/groups/${groupId}/leave`, {}, { headers });
      navigate('/groups');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to leave group');
    }
  };

  // ── Save settings ────────────────────────────────────────────────────────
  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMsg('');
    setSaveErr('');
    try {
      await axios.put(`${API_BASE}/groups/${groupId}`, {
        ...editForm,
        max_members: parseInt(editForm.max_members) || 50,
        sport_filter: editForm.sport_filter || null,
      }, { headers });
      setSaveMsg('Group settings saved!');
      fetchGroup();
    } catch (e) {
      setSaveErr(e.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete group ─────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_BASE}/groups/${groupId}`, { headers });
      navigate('/groups');
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to delete group');
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  // ── Kick member ──────────────────────────────────────────────────────────
  const handleKick = async (userId, username) => {
    if (!window.confirm(`Remove @${username} from the group?`)) return;
    try {
      await axios.delete(`${API_BASE}/groups/${groupId}/members/${userId}/kick`, { headers });
      fetchGroup();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to kick member');
    }
  };

  // ── Set role ─────────────────────────────────────────────────────────────
  const handleSetRole = async (userId, newRole) => {
    try {
      await axios.put(`${API_BASE}/groups/${groupId}/members/${userId}/role`, { role: newRole }, { headers });
      fetchGroup();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to set role');
    }
  };

  // ── Join requests ────────────────────────────────────────────────────────
  const handleJoinRequest = async (requestId, action) => {
    try {
      await axios.post(
        `${API_BASE}/groups/${groupId}/join-requests/${requestId}/respond`,
        { action },
        { headers }
      );
      fetchGroup();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to process request');
    }
  };

  // ── Invite user search ───────────────────────────────────────────────────
  const runInviteSearch = useCallback(async (q) => {
    if (!q.trim()) { setInviteResults([]); return; }
    setInviteLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/data/search`, { params: { q } });
      const members = (res.data?.members || []).filter(m => m.username !== currentUser?.username);
      setInviteResults(members);
    } catch {
      setInviteResults([]);
    } finally {
      setInviteLoading(false);
    }
  }, [currentUser?.username]);

  const handleInviteSearch = (val) => {
    setInviteQ(val);
    clearTimeout(inviteTimer.current);
    inviteTimer.current = setTimeout(() => runInviteSearch(val), 300);
  };

  const handleSendInvite = async (user) => {
    try {
      const res = await axios.post(`${API_BASE}/groups/${groupId}/invite`, { user_id: user.id }, { headers });
      setInviteSent(prev => ({ ...prev, [user.id]: res.data.message }));
    } catch (e) {
      setInviteSent(prev => ({ ...prev, [user.id]: e.response?.data?.error || 'Error' }));
    }
  };

  // ── Regenerate invite link ───────────────────────────────────────────────
  const handleRegenLink = async () => {
    if (!window.confirm('Regenerate the invite link? The old link will stop working.')) return;
    setRegenLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/groups/${groupId}/regenerate-link`, {}, { headers });
      const newLink = `${window.location.origin}/groups/join/${res.data.invite_token}`;
      setInviteLink(newLink);
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to regenerate link');
    } finally {
      setRegenLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // ── Game picker helpers ──────────────────────────────────────────────────
  const fetchAvailGames = useCallback(async (q = '', sport = '', page = 1) => {
    setAvailLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/groups/${groupId}/available-games`, {
        headers,
        params: { q, sport, page },
      });
      const data = res.data;
      setAvailGames(prev => page === 1 ? data.games : [...prev, ...data.games]);
      setAvailPage(page);
      setAvailHasNext(data.has_next);
      setSelectedIds(new Set(data.selected_ids || []));
    } catch {
      // silently ignore
    } finally {
      setAvailLoading(false);
    }
  }, [groupId, headers]);

  const handleGameSearch = (val) => {
    setGameQ(val);
    clearTimeout(gameTimer.current);
    gameTimer.current = setTimeout(() => fetchAvailGames(val, gameSport, 1), 350);
  };

  const handleGameSportFilter = (sport) => {
    const next = sport === gameSport ? '' : sport;
    setGameSport(next);
    fetchAvailGames(gameQ, next, 1);
  };

  const handleToggleGame = async (gameId, currentlySelected) => {
    setGameMsg('');
    try {
      if (currentlySelected) {
        await axios.delete(`${API_BASE}/groups/${groupId}/games/${gameId}`, { headers });
        setSelectedIds(prev => { const s = new Set(prev); s.delete(gameId); return s; });
        setAvailGames(prev => prev.map(g => g.game_id === gameId ? { ...g, selected: false } : g));
        setGroupGames(prev => prev.filter(g => g.game_id !== gameId));
      } else {
        await axios.post(`${API_BASE}/groups/${groupId}/games`, { game_id: gameId }, { headers });
        setSelectedIds(prev => new Set([...prev, gameId]));
        setAvailGames(prev => prev.map(g => g.game_id === gameId ? { ...g, selected: true } : g));
        // Refresh the schedule view
        fetchGroupGames();
      }
    } catch (e) {
      setGameMsg(e.response?.data?.error || 'Error updating game');
    }
  };

  // ── Preset helpers ────────────────────────────────────────────────────────
  const fetchGroupGames = useCallback(async () => {
    setGroupGamesLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/groups/${groupId}/games`, { headers });
      setGroupGames(res.data.games || []);
      setSelectedIds(new Set((res.data.games || []).map(g => g.game_id)));
    } catch {
      // silently ignore
    } finally {
      setGroupGamesLoading(false);
    }
  }, [groupId, headers]);

  const handlePreset = async (preset, value = '') => {
    setPresetLoading(true);
    setPresetMsg('');
    setPresetErr('');
    try {
      const res = await axios.post(
        `${API_BASE}/groups/${groupId}/games/preset`,
        { preset, value },
        { headers }
      );
      setPresetMsg(res.data.message);
      fetchGroupGames();
      fetchAvailGames(gameQ, gameSport, 1);
    } catch (e) {
      setPresetErr(e.response?.data?.error || 'Failed to apply preset');
    } finally {
      setPresetLoading(false);
    }
  };

  // ── Formula helpers ──────────────────────────────────────────────────────
  const FORMULA_TOKENS = ['wins', 'losses', '+', '-', '*', '/', '(', ')'];
  const FORMULA_NUMBERS = ['0','1','2','3','4','5','6','7','8','9'];

  const FORMULA_PRESETS = [
    { label: 'Win %',          formula: 'wins / (wins + losses)' },
    { label: 'Wins Only',      formula: 'wins' },
    { label: 'Win − Loss',     formula: 'wins - losses' },
    { label: 'Win × 2 − Loss', formula: 'wins * 2 - losses' },
    { label: '2W − L + Pct',   formula: 'wins * 2 - losses + wins / (wins + losses)' },
  ];

  const appendFormula = (token) => {
    if (token === '⌫') {
      setFormula(prev => prev.trimEnd().replace(/(\w+|[\d.]+|[+\-*/()]\s*)$/, '').trimEnd());
    } else if (['wins','losses'].includes(token)) {
      setFormula(prev => (prev ? prev + ' ' + token : token));
    } else if (['+','-','*','/'].includes(token)) {
      setFormula(prev => (prev ? prev + ' ' + token : token));
    } else {
      setFormula(prev => prev + token);
    }
  };

  const previewFormula = () => {
    if (!formula.trim()) return null;
    try {
      // simple client-side eval preview using safe substitution
      const expr = formula.replace(/\bwins\b/g, '5').replace(/\blosses\b/g, '3');
      // only allow safe chars before evaling
      if (!/^[\d\s+\-*/().]+$/.test(expr)) return null;
      // eslint-disable-next-line no-new-func
      const result = Function(`"use strict"; return (${expr})`)();
      return typeof result === 'number' && isFinite(result) ? result.toFixed(1) : null;
    } catch { return null; }
  };

  const handleSaveFormula = async () => {
    setFormulaSaving(true);
    setFormulaMsg('');
    setFormulaErr('');
    try {
      const res = await axios.put(`${API_BASE}/groups/${groupId}/formula`, { formula: formula.trim() }, { headers });
      setFormulaMsg(res.data.message);
      fetchGroup();
    } catch (e) {
      setFormulaErr(e.response?.data?.error || 'Failed to save formula');
    } finally {
      setFormulaSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <BaseLayout>
        <div className="grp-wrap grp-center">
          <FlameLoader text="Loading group..." />
        </div>
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout>
        <div className="grp-wrap grp-center">
          <p style={{ color: 'var(--red)' }}>{error}</p>
          <button className="grp-btn grp-btn--outline" onClick={() => navigate('/groups')}>
            Back to Groups
          </button>
        </div>
      </BaseLayout>
    );
  }

  const isMember = !!myRole;
  const isAdmin  = myRole === 'owner' || myRole === 'admin';
  const isOwner  = myRole === 'owner';

  const handleTabChange = (tabId) => {
    if (tabId === 'picks') {
      navigate(`/groups/${groupId}/picks`);
      return;
    }
    setActiveTab(tabId);
    if (tabId === 'games') {
      fetchGroupGames();
      if (isAdmin && availGames.length === 0) fetchAvailGames('', '', 1);
    }
  };

  const tabs = [
    { id: 'leaderboard', label: 'Leaderboard', icon: 'fa-trophy' },
    { id: 'games',       label: 'Games',       icon: 'fa-calendar' },
    { id: 'members',     label: 'Members',     icon: 'fa-users' },
    ...(isMember ? [{ id: 'picks',    label: 'Picks',    icon: 'fa-fire'    }] : []),
    ...(isMember ? [{ id: 'settings', label: 'Settings', icon: 'fa-cog'    }] : []),
  ];

  return (
    <BaseLayout>
      <div className="grp-wrap">

        {/* ── Group header ──────────────────────────────────────────────── */}
        <div className="grp-detail-header">
          <GroupAvatar name={group.name} avatarUrl={group.avatar_url} size={72} />
          <div className="grp-detail-info">
            <div className="grp-detail-title-row">
              <h1 className="grp-detail-name">{group.name}</h1>
              {group.sport_filter && (
                <span className="grp-badge grp-badge--sport">{group.sport_filter}</span>
              )}
              {!group.is_public && (
                <span className="grp-badge grp-badge--private"><i className="fa fa-lock" /> Private</span>
              )}
            </div>
            {group.description && <p className="grp-detail-desc">{group.description}</p>}
            <div className="grp-detail-meta">
              <span><i className="fa fa-users" /> {group.member_count}{group.max_members ? `/${group.max_members}` : ''} members</span>
              <span><i className="fa fa-calendar" /> Created {group.created_at ? new Date(group.created_at + 'Z').toLocaleDateString() : '—'}</span>
              <span><i className="fa fa-globe" /> {JOIN_POLICY_LABELS[group.join_policy] || group.join_policy}</span>
              {myRole && (
                <span className={`grp-role-pill grp-role-pill--${myRole}`} style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {myRole === 'owner' && <i className="fa fa-crown" style={{ marginRight: 4 }} />}
                  {myRole === 'admin' && <i className="fa fa-shield" style={{ marginRight: 4 }} />}
                  {myRole}
                </span>
              )}
            </div>
          </div>
          <div className="grp-detail-actions">
            <button className="grp-btn grp-btn--outline" onClick={() => navigate('/groups')}>
              <i className="fa fa-arrow-left" /> Groups
            </button>
            {isMember && myRole !== 'owner' && (
              <button className="grp-btn grp-btn--danger-outline" onClick={handleLeave}>
                Leave
              </button>
            )}
            {!isMember && group.join_policy !== 'invite_only' && (
              <button className="grp-btn grp-btn--primary" onClick={handleJoin} disabled={joining}>
                {joining ? <i className="fa fa-spinner fa-spin" /> : (
                  group.join_policy === 'request' ? 'Request to Join' : 'Join Group'
                )}
              </button>
            )}
          </div>
        </div>

        {joinFeedback && (
          <div className={`grp-feedback${joinFeedback.includes('fail') || joinFeedback.includes('error') ? ' grp-feedback--err' : ' grp-feedback--ok'}`}>
            {joinFeedback}
          </div>
        )}

        {/* ── Join requests banner (admin) ──────────────────────────────── */}
        {isAdmin && joinRequests.length > 0 && (
          <div className="grp-requests-banner">
            <span className="grp-requests-label">
              <i className="fa fa-hand-o-up" /> {joinRequests.length} pending join request{joinRequests.length > 1 ? 's' : ''}
            </span>
            <button className="grp-btn grp-btn--outline grp-btn--sm" onClick={() => setActiveTab('members')}>
              Review
            </button>
          </div>
        )}

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div className="grp-tabs">
          {tabs.map(t => (
            <button
              key={t.id}
              className={`grp-tab${activeTab === t.id ? ' grp-tab--active' : ''}`}
              onClick={() => handleTabChange(t.id)}
            >
              <i className={`fa ${t.icon}`} /> {t.label}
            </button>
          ))}
        </div>

        {/* ── Leaderboard ───────────────────────────────────────────────── */}
        {activeTab === 'leaderboard' && (
          <div className="grp-panel">

            {/* Toolbar: info toggle + refresh */}
            <div className="grp-lb-toolbar">
              <button
                className={`grp-lb-info-btn${lbInfoOpen ? ' grp-lb-info-btn--active' : ''}`}
                onClick={() => setLbInfoOpen(o => !o)}
              >
                <i className="fa fa-info-circle" /> Ranking Info
                <i className={`fa fa-chevron-${lbInfoOpen ? 'up' : 'down'} grp-lb-info-chevron`} />
              </button>
              <button
                className="grp-btn grp-btn--outline grp-btn--sm"
                onClick={refreshLeaderboard}
                disabled={lbRefreshing}
                title="Refresh leaderboard"
              >
                <i className={`fa fa-refresh${lbRefreshing ? ' fa-spin' : ''}`} />
                {lbRefreshing ? ' Refreshing…' : ' Refresh'}
              </button>
            </div>

            {/* Collapsible info panel */}
            {lbInfoOpen && (
              <div className="grp-lb-info-panel">
                <div className="grp-lb-info-row">
                  <i className="fa fa-filter grp-lb-info-icon" />
                  <div className="grp-lb-info-content">
                    <span className="grp-lb-info-label">Game Scope</span>
                    <span className="grp-lb-info-value">
                      {group.game_count > 0
                        ? <><strong>{group.game_count}</strong> Curated Games. Only picks on group-selected games count toward scores
                        </>
                        : 'All Games. Every pick from all time counts toward scores'
                      }
                    </span>
                  </div>
                </div>
                <div className="grp-lb-info-row">
                  <i className="fa fa-superscript grp-lb-info-icon" />
                  <div className="grp-lb-info-content">
                    <span className="grp-lb-info-label">Ranking Formula</span>
                    <span className="grp-lb-info-value">
                      {group.score_formula
                        ? <code className="grp-lb-info-code">{group.score_formula}</code>
                        : 'Standard Flame Picks Formula'
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}

            {leaderboard.length === 0 ? (
              <div className="grp-empty">
                <i className="fa fa-trophy grp-empty-icon" />
                <p>No picks data yet — leaderboard updates after games are scored.</p>
              </div>
            ) : (() => {
              // Compute competition ranks with tie detection client-side
              const tieKey = (e) => `${e.computed}_${e.win_pct}`;
              let compRank = 1;
              const ranked = leaderboard.map((entry, idx, arr) => {
                if (idx > 0 && tieKey(entry) !== tieKey(arr[idx - 1])) {
                  compRank = idx + 1;
                }
                return { ...entry, displayRank: compRank };
              });
              const withTied = ranked.map(entry => ({
                ...entry,
                tied: ranked.filter(e => e.displayRank === entry.displayRank).length > 1,
              }));

              return (
                <div className="grp-lb-wrap">
                <div className="grp-lb">
                  {/* Table header */}
                  <div className="grp-lb-header">
                    <span className="grp-lb-col grp-lb-col--rank">#</span>
                    <span className="grp-lb-col grp-lb-col--player">Player</span>
                    <span className="grp-lb-col grp-lb-col--num">W</span>
                    <span className="grp-lb-col grp-lb-col--num">L</span>
                    <span className="grp-lb-col grp-lb-col--num">Win%</span>
                    <span className="grp-lb-col grp-lb-col--num grp-lb-col--score">Score</span>
                  </div>
                  {withTied.map((entry) => {
                    const isMe = entry.user_id === currentUser?.id;
                    const medalCls = entry.displayRank === 1 ? ' grp-lb-row--top1'
                                   : entry.displayRank === 2 ? ' grp-lb-row--top2'
                                   : entry.displayRank === 3 ? ' grp-lb-row--top3'
                                   : '';
                    return (
                      <div key={entry.user_id} className={`grp-lb-row${isMe ? ' grp-lb-row--me' : ''}${medalCls}`}>
                        <span className={`grp-lb-col grp-lb-col--rank${entry.displayRank === 1 ? ' lb-rank--gold' : entry.displayRank === 2 ? ' lb-rank--silver' : entry.displayRank === 3 ? ' lb-rank--bronze' : ''}`}>
                          {entry.tied ? '=' : ''}#{entry.displayRank}
                        </span>
                        <span className="grp-lb-col grp-lb-col--player">
                          <MemberAvatar
                            name={`${entry.firstName} ${entry.lastName}`}
                            picture={entry.profile_picture}
                            size={34}
                          />
                          <div className="grp-lb-player-info">
                            <Link to={`/user/${entry.username}`} className="grp-lb-username">
                              @{entry.username}
                            </Link>
                            <span className="grp-lb-fullname">{entry.firstName} {entry.lastName}</span>
                          </div>
                          {entry.role === 'owner' && <span className="grp-lb-crown"><i className="fa fa-crown" style={{ color: '#f5c518', fontSize: '0.75rem' }} /></span>}
                          {entry.role === 'admin' && <span className="grp-lb-crown"><i className="fa fa-shield" style={{ color: '#818cf8', fontSize: '0.75rem' }} /></span>}
                          {isMe && <span className="grp-lb-you">You</span>}
                        </span>
                        <span className="grp-lb-col grp-lb-col--num grp-lb-wins">{entry.score}</span>
                        <span className="grp-lb-col grp-lb-col--num grp-lb-losses">{entry.losses}</span>
                        <span className="grp-lb-col grp-lb-col--num grp-lb-pct">{entry.win_pct}%</span>
                        <span className="grp-lb-col grp-lb-col--num grp-lb-col--score grp-lb-computed">{entry.computed}</span>
                      </div>
                    );
                  })}
                </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Games ─────────────────────────────────────────────────────── */}
        {activeTab === 'games' && (
          <div className="grp-panel">

            {/* Schedule — visible to all members */}
            <div className="grp-section">
              <h3 className="grp-section-label">
                <i className="fa fa-calendar" /> Group Schedule
                {selectedIds.size > 0 && (
                  <span className="grp-count-pill">{selectedIds.size}</span>
                )}
              </h3>
              <p className="grp-section-hint">
                Games curated for this group. Only picks on these games count toward group scores.
              </p>
              {groupGamesLoading ? (
                <div className="grp-loading" style={{ padding: '18px 0' }}>
                  <i className="fa fa-spinner fa-spin" />
                </div>
              ) : groupGames.length === 0 ? (
                <div className="grp-schedule-empty">
                  <i className="fa fa-calendar-o" />
                  <p>{isAdmin ? 'No games added yet. Use the controls below to add games.' : 'No games have been selected for this group yet.'}</p>
                </div>
              ) : (
                <div className="grp-game-list">
                  {groupGames.map(gm => {
                    const finished = !!gm.winning_team;
                    return (
                      <div key={gm.game_id} className={`grp-game-row grp-game-row--selected${!finished ? ' grp-game-row--upcoming' : ''}`}>
                        <div className="grp-game-info grp-game-info--clickable" onClick={() => navigate(`/game/${gm.game_id}`)}>
                          <span className="grp-game-badge grp-game-badge--sport">{gm.sport}</span>
                          <span className="grp-game-matchup">{gm.away_team} @ {gm.home_team}</span>
                          <span className="grp-game-date">{gm.date}&nbsp;&nbsp;{gm.time}</span>
                          {finished
                            ? <span className="grp-game-badge grp-game-badge--final">Final: {gm.score}</span>
                            : <span className="grp-game-badge grp-game-badge--upcoming">Upcoming</span>
                          }
                          <i className="fa fa-external-link grp-game-link-icon" />
                        </div>
                        {isAdmin && (
                          <button
                            className="grp-btn grp-btn--danger-outline grp-btn--sm"
                            onClick={() => handleToggleGame(gm.game_id, true)}
                          >
                            <i className="fa fa-minus" /> Remove
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add Games — admin/owner only */}
            {isAdmin && (
              <>
                {/* Quick Add Presets */}
                <div className="grp-section">
                  <h3 className="grp-section-label"><i className="fa fa-bolt" /> Quick Add Presets</h3>
                  <p className="grp-section-hint">Bulk-add games by sport, team, or status in one click.</p>

                  {presetMsg && <p className="grp-feedback grp-feedback--ok">{presetMsg}</p>}
                  {presetErr && <p className="grp-feedback grp-feedback--err">{presetErr}</p>}

                  <div className="grp-preset-bar">
                    {SPORTS.map(s => (
                      <button key={`all-${s}`} className="grp-preset-chip"
                        disabled={presetLoading} onClick={() => handlePreset('sport', s)}>
                        <i className="fa fa-plus" /> All {s}
                      </button>
                    ))}
                    {SPORTS.map(s => (
                      <button key={`upcoming-${s}`} className="grp-preset-chip"
                        disabled={presetLoading} onClick={() => handlePreset('upcoming_sport', s)}>
                        <i className="fa fa-clock-o" /> Upcoming {s}
                      </button>
                    ))}
                    <button className="grp-preset-chip" disabled={presetLoading}
                      onClick={() => handlePreset('upcoming')}>
                      <i className="fa fa-clock-o" /> All Upcoming
                    </button>
                    <button className="grp-preset-chip grp-preset-chip--danger" disabled={presetLoading}
                      onClick={() => {
                        if (window.confirm('Remove all curated games from this group?')) handlePreset('clear');
                      }}>
                      <i className="fa fa-times" /> Clear All
                    </button>
                  </div>

                  {/* Team preset */}
                  <div className="grp-preset-team-row">
                    <div className="grp-search-wrap" style={{ flex: 1 }}>
                      <i className="fa fa-search grp-search-icon" />
                      <input
                        className="grp-search-input"
                        placeholder="Team name (e.g. Lakers, Arsenal)…"
                        value={presetTeamQ}
                        onChange={e => setPresetTeamQ(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' && presetTeamQ.trim()) {
                            handlePreset('team', presetTeamQ.trim());
                            setPresetTeamQ('');
                          }
                        }}
                      />
                    </div>
                    <button
                      className="grp-btn grp-btn--primary"
                      disabled={presetLoading || !presetTeamQ.trim()}
                      onClick={() => { handlePreset('team', presetTeamQ.trim()); setPresetTeamQ(''); }}
                    >
                      {presetLoading
                        ? <i className="fa fa-spinner fa-spin" />
                        : <><i className="fa fa-plus" /> Add All Team Games</>}
                    </button>
                  </div>
                </div>

                {/* Manual Game Picker */}
                <div className="grp-section">
                  <h3 className="grp-section-label">
                    <i className="fa fa-search" /> Browse &amp; Add Games
                    {selectedIds.size > 0 && (
                      <span className="grp-count-pill">{selectedIds.size} selected</span>
                    )}
                  </h3>
                  <div className="grp-game-picker-bar">
                    <div className="grp-search-wrap" style={{ flex: 1 }}>
                      <i className="fa fa-search grp-search-icon" />
                      <input className="grp-search-input" placeholder="Search teams…"
                        value={gameQ} onChange={e => handleGameSearch(e.target.value)} />
                    </div>
                    <div className="grp-sport-filters" style={{ marginTop: 0 }}>
                      {SPORTS.map(s => (
                        <button key={s}
                          className={`grp-filter-chip${gameSport === s ? ' grp-filter-chip--active' : ''}`}
                          onClick={() => handleGameSportFilter(s)}>{s}
                        </button>
                      ))}
                    </div>
                  </div>
                  {gameMsg && <p className="grp-feedback grp-feedback--err">{gameMsg}</p>}
                  {availLoading && availGames.length === 0 ? (
                    <div className="grp-loading" style={{ padding: '18px 0' }}>
                      <i className="fa fa-spinner fa-spin" />
                    </div>
                  ) : (
                    <div className="grp-game-list">
                      {availGames.map(gm => {
                        const sel = selectedIds.has(gm.game_id);
                        const finished = !!gm.winning_team;
                        return (
                          <div key={gm.game_id} className={`grp-game-row${sel ? ' grp-game-row--selected' : ''}`}>
                            <div className="grp-game-info">
                              <span className="grp-game-badge grp-game-badge--sport">{gm.sport}</span>
                              <span className="grp-game-matchup">{gm.away_team} @ {gm.home_team}</span>
                              <span className="grp-game-date">{gm.date}&nbsp;&nbsp;{gm.time}</span>
                              {finished && (
                                <span className="grp-game-badge grp-game-badge--final">Final: {gm.score}</span>
                              )}
                            </div>
                            <button
                              className={`grp-btn grp-btn--sm ${sel ? 'grp-btn--danger-outline' : 'grp-btn--outline'}`}
                              onClick={() => handleToggleGame(gm.game_id, sel)}
                            >
                              {sel
                                ? <><i className="fa fa-minus" /> Remove</>
                                : <><i className="fa fa-plus" /> Add</>}
                            </button>
                          </div>
                        );
                      })}
                      {availGames.length === 0 && !availLoading && (
                        <p className="grp-section-hint" style={{ padding: '12px 0' }}>No games found.</p>
                      )}
                      {availHasNext && (
                        <button className="grp-btn grp-btn--outline grp-load-more"
                          disabled={availLoading}
                          onClick={() => fetchAvailGames(gameQ, gameSport, availPage + 1)}>
                          {availLoading ? <i className="fa fa-spinner fa-spin" /> : 'Load More'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        )}

        {/* ── Members ───────────────────────────────────────────────────── */}
        {activeTab === 'members' && (
          <div className="grp-panel">
            {/* Pending join requests */}
            {isAdmin && joinRequests.length > 0 && (
              <div className="grp-section">
                <h3 className="grp-section-label">
                  <i className="fa fa-hand-o-up" /> Pending Requests
                  <span className="grp-count-pill">{joinRequests.length}</span>
                </h3>
                <div className="grp-members-list">
                  {joinRequests.map(req => (
                    <div key={req.request_id} className="grp-member-row">
                      <MemberAvatar name={`${req.firstName} ${req.lastName}`} picture={req.profile_picture} />
                      <div className="grp-member-info">
                        <Link to={`/user/${req.username}`} className="grp-member-name">@{req.username}</Link>
                        <span className="grp-member-sub">Requested {req.created_at ? new Date(req.created_at + 'Z').toLocaleDateString() : '—'}</span>
                      </div>
                      <div className="grp-member-actions">
                        <button className="grp-btn grp-btn--accept grp-btn--sm"
                          onClick={() => handleJoinRequest(req.request_id, 'approve')}>
                          <i className="fa fa-check" /> Approve
                        </button>
                        <button className="grp-btn grp-btn--decline grp-btn--sm"
                          onClick={() => handleJoinRequest(req.request_id, 'deny')}>
                          Deny
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Member list */}
            <div className="grp-section">
              <h3 className="grp-section-label">
                <i className="fa fa-users" /> All Members
                <span className="grp-count-pill">{leaderboard.length}</span>
              </h3>
              <div className="grp-members-list">
                {leaderboard.map(entry => (
                  <div key={entry.user_id} className="grp-member-row">
                    <MemberAvatar
                      name={`${entry.firstName} ${entry.lastName}`}
                      picture={entry.profile_picture}
                    />
                    <div className="grp-member-info">
                      <Link to={`/user/${entry.username}`} className="grp-member-name">
                        @{entry.username}
                        {entry.role === 'owner' && <i className="fa fa-crown" style={{ color: '#f5c518', fontSize: '0.75rem', marginLeft: 5 }} />}
                        {entry.role === 'admin' && <i className="fa fa-shield" style={{ color: '#818cf8', fontSize: '0.75rem', marginLeft: 5 }} />}
                        {entry.user_id === currentUser?.id && (
                          <span className="grp-lb-you" style={{ marginLeft: 6 }}>You</span>
                        )}
                      </Link>
                      <span className="grp-member-sub">
                        {entry.score}W / {entry.losses}L · {entry.win_pct}% · Joined {entry.joined_at ? new Date(entry.joined_at + 'Z').toLocaleDateString() : '—'}
                      </span>
                    </div>
                    {isAdmin && entry.user_id !== currentUser?.id && entry.role !== 'owner' && (
                      <div className="grp-member-actions">
                        {isOwner && entry.role === 'member' && (
                          <button className="grp-btn grp-btn--outline grp-btn--sm"
                            onClick={() => handleSetRole(entry.user_id, 'admin')}
                            title="Promote to Admin">
                            <i className="fa fa-star" /> Admin
                          </button>
                        )}
                        {isOwner && entry.role === 'admin' && (
                          <button className="grp-btn grp-btn--outline grp-btn--sm"
                            onClick={() => handleSetRole(entry.user_id, 'member')}
                            title="Demote to Member">
                            <i className="fa fa-star-o" /> Member
                          </button>
                        )}
                        <button className="grp-btn grp-btn--danger-outline grp-btn--sm"
                          onClick={() => handleKick(entry.user_id, entry.username)}>
                          <i className="fa fa-times" /> Kick
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Settings ──────────────────────────────────────────────────── */}
        {activeTab === 'settings' && isMember && (
          <div className="grp-panel">

            {/* ── My Notifications (non-owner members only) ────────────── */}
            {!isOwner && <div className="grp-section">
              <h3 className="grp-section-label"><i className="fa fa-bell" /> My Notifications</h3>
              <p className="grp-section-hint">
                Control which events in this group send you an inbox message.
              </p>
              <div className="grp-notify-row">
                <div className="grp-notify-info">
                  <span className="grp-notify-label">Group Alerts</span>
                  <span className="grp-notify-desc">
                    Receive a message when you are kicked, granted admin, or have admin revoked.
                  </span>
                </div>
                <button
                  type="button"
                  className={`grp-toggle-switch${myNotify ? ' grp-toggle-switch--on' : ''}`}
                  disabled={notifySaving}
                  onClick={() => handleToggleMyNotify(!myNotify)}
                  title={myNotify ? 'Turn off' : 'Turn on'}
                >
                  <span className="grp-toggle-thumb" />
                </button>
              </div>
            </div>}

            {/* Edit group — admin/owner only */}
            {isAdmin && (
            <div className="grp-section">
              <h3 className="grp-section-label"><i className="fa fa-pencil" /> Edit Group</h3>

              {/* ── Avatar picker ─────────────────────────────────────── */}
              <div className="grp-avatar-upload-row">
                <button
                  type="button"
                  className="grp-avatar-upload-btn"
                  title="Change group image"
                  disabled={avatarUploading}
                  onClick={() => avatarInputRef.current?.click()}
                >
                  <GroupAvatar name={group.name} avatarUrl={group.avatar_url} size={72} />
                  <span className="grp-avatar-upload-overlay">
                    {avatarUploading
                      ? <i className="fa fa-spinner fa-spin" />
                      : <i className="fa fa-camera" />}
                  </span>
                </button>
                <div className="grp-avatar-upload-info">
                  <span className="grp-avatar-upload-label">Group Image</span>
                  <span className="grp-avatar-upload-hint">PNG, JPG, GIF or WebP · max 10 MB</span>
                  {avatarErr && <span className="grp-feedback grp-feedback--err">{avatarErr}</span>}
                </div>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  style={{ display: 'none' }}
                  onChange={handleAvatarUpload}
                />
              </div>

              <form className="grp-settings-form" onSubmit={handleSaveSettings}>
                <label className="grp-label">Group Name *</label>
                <input className="grp-input" maxLength={50} required value={editForm.name || ''}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />

                <label className="grp-label">Description</label>
                <textarea className="grp-input grp-textarea" rows={3} maxLength={200}
                  value={editForm.description || ''}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />

                {isOwner && (
                  <>
                    <div className="grp-form-row">
                      <div className="grp-form-col">
                        <label className="grp-label">Sport Focus</label>
                        <select className="grp-input grp-select" value={editForm.sport_filter || ''}
                          onChange={e => setEditForm(f => ({ ...f, sport_filter: e.target.value }))}>
                          <option value="">All Sports</option>
                          {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <div className="grp-form-col">
                        <label className="grp-label">Max Members</label>
                        <input className="grp-input" type="number" min={2} max={100}
                          value={editForm.max_members || 50}
                          onChange={e => setEditForm(f => ({ ...f, max_members: e.target.value }))} />
                      </div>
                    </div>

                    <label className="grp-label">Join Policy</label>
                    <div className="grp-radio-group">
                      {[
                        { v: 'open',        label: 'Open',        desc: 'Anyone can join' },
                        { v: 'request',     label: 'Request',     desc: 'Members must be approved' },
                        { v: 'invite_only', label: 'Invite Only', desc: 'By invite link or direct invite only' },
                      ].map(opt => (
                        <label key={opt.v} className={`grp-radio-opt${editForm.join_policy === opt.v ? ' grp-radio-opt--active' : ''}`}>
                          <input type="radio" name="ep" value={opt.v}
                            checked={editForm.join_policy === opt.v}
                            onChange={() => setEditForm(f => ({ ...f, join_policy: opt.v }))} />
                          <div>
                            <span className="grp-radio-label">{opt.label}</span>
                            <span className="grp-radio-desc">{opt.desc}</span>
                          </div>
                        </label>
                      ))}
                    </div>

                    <label className="grp-label">Visibility</label>
                    <div className="grp-toggle-row">
                      <button type="button"
                        className={`grp-toggle-btn${editForm.is_public ? ' grp-toggle-btn--active' : ''}`}
                        onClick={() => setEditForm(f => ({ ...f, is_public: true }))}>
                        <i className="fa fa-globe" /> Public
                      </button>
                      <button type="button"
                        className={`grp-toggle-btn${!editForm.is_public ? ' grp-toggle-btn--active' : ''}`}
                        onClick={() => setEditForm(f => ({ ...f, is_public: false }))}>
                        <i className="fa fa-lock" /> Private
                      </button>
                    </div>
                  </>
                )}

                {saveMsg && <p className="grp-feedback grp-feedback--ok">{saveMsg}</p>}
                {saveErr && <p className="grp-feedback grp-feedback--err">{saveErr}</p>}

                <button type="submit" className="grp-btn grp-btn--primary" disabled={saving}>
                  {saving ? <i className="fa fa-spinner fa-spin" /> : 'Save Changes'}
                </button>
              </form>
            </div>
            )} {/* end isAdmin edit group */}

            {/* Invite link — admin only */}
            {isAdmin && <div className="grp-section">
              <h3 className="grp-section-label"><i className="fa fa-link" /> Invite Link</h3>
              <p className="grp-section-hint">Share this link to invite anyone to the group directly.</p>
              {inviteLink ? (
                <div className="grp-link-row">
                  <input className="grp-input grp-link-input" readOnly value={inviteLink} />
                  <button className="grp-btn grp-btn--outline" onClick={handleCopyLink}>
                    {linkCopied ? <><i className="fa fa-check" /> Copied!</> : <><i className="fa fa-copy" /> Copy</>}
                  </button>
                  <button className="grp-btn grp-btn--outline" onClick={handleRegenLink} disabled={regenLoading}
                    title="Regenerate link (invalidates old one)">
                    <i className={`fa fa-refresh${regenLoading ? ' fa-spin' : ''}`} />
                  </button>
                </div>
              ) : (
                <p className="grp-section-hint">No invite link generated yet.</p>
              )}
            </div>}

            {/* Invite user — admin only */}
            {isAdmin && <div className="grp-section">
              <h3 className="grp-section-label"><i className="fa fa-user-plus" /> Invite a Player</h3>
              <div className="grp-search-wrap" style={{ marginBottom: 12 }}>
                <i className="fa fa-search grp-search-icon" />
                <input className="grp-search-input" placeholder="Search by username…"
                  value={inviteQ} onChange={e => handleInviteSearch(e.target.value)} />
              </div>
              {inviteLoading && <div className="grp-loading" style={{ padding: '12px 0' }}><i className="fa fa-spinner fa-spin" /></div>}
              {inviteResults.length > 0 && (
                <div className="grp-members-list">
                  {inviteResults.map(u => {
                    const alreadyMember = leaderboard.some(e => e.user_id === u.id);
                    const sent = inviteSent[u.id];
                    return (
                      <div key={u.id} className="grp-member-row">
                        <MemberAvatar name={`${u.firstName} ${u.lastName}`} picture={u.profile_picture} />
                        <div className="grp-member-info">
                          <span className="grp-member-name">@{u.username}</span>
                          <span className="grp-member-sub">{u.firstName} {u.lastName}</span>
                        </div>
                        <div className="grp-member-actions">
                          {alreadyMember ? (
                            <span className="grp-badge" style={{ color: 'var(--text-dim)' }}>Member</span>
                          ) : sent ? (
                            <span className="grp-badge" style={{ color: sent.includes('err') || sent.includes('fail') || sent.includes('lready') ? 'var(--red)' : 'var(--green)' }}>
                              {sent}
                            </span>
                          ) : (
                            <button className="grp-btn grp-btn--primary grp-btn--sm"
                              onClick={() => handleSendInvite(u)}>
                              <i className="fa fa-paper-plane" /> Invite
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>}

            {/* ── Owner Notifications ───────────────────────────────────── */}
            {isOwner && (
              <div className="grp-section">
                <h3 className="grp-section-label"><i className="fa fa-bell-o" /> Owner Notifications</h3>
                <p className="grp-section-hint">
                  Get an inbox message whenever someone joins or leaves this group.
                </p>
                <div className="grp-notify-row">
                  <div className="grp-notify-info">
                    <span className="grp-notify-label">Join / Leave Alerts</span>
                    <span className="grp-notify-desc">
                      Receive a message when a member joins or leaves.
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`grp-toggle-switch${group.notify_owner_join_leave ? ' grp-toggle-switch--on' : ''}`}
                    disabled={notifySaving}
                    onClick={() => handleToggleOwnerNotify(!group.notify_owner_join_leave)}
                    title={group.notify_owner_join_leave ? 'Turn off' : 'Turn on'}
                  >
                    <span className="grp-toggle-thumb" />
                  </button>
                </div>
              </div>
            )}

            {/* ── Scoring Formula (owner only) ──────────────────────────── */}
            {isOwner && (
              <div className="grp-section">
                <h3 className="grp-section-label">
                  <i className="fa fa-superscript" /> Scoring Formula
                </h3>
                <p className="grp-section-hint">
                  Customise how points are calculated. Use <code>wins</code> and <code>losses</code> with +, −, ×, ÷, and numbers. Leave blank to use the Standard Flame Picks Formula.
                </p>

                {/* Formula display */}
                <div className="grp-formula-display">
                  {formula || <span className="grp-formula-placeholder">Standard Flame Picks Formula (default)</span>}
                </div>

                {/* Preset formulas */}
                <div className="grp-formula-preset-bar">
                  <span className="grp-formula-preset-label">Presets:</span>
                  {FORMULA_PRESETS.map(p => (
                    <button key={p.label} className="grp-formula-preset-chip"
                      onClick={() => setFormula(p.formula)}>
                      {p.label}
                    </button>
                  ))}
                </div>

                {/* Token pills */}
                <div className="grp-formula-tokens">
                  {FORMULA_TOKENS.map(t => (
                    <button key={t} className="grp-formula-token" onClick={() => appendFormula(t)}>
                      {t}
                    </button>
                  ))}
                  {/* Backspace — fa-backspace is not in FA4; use arrow-left */}
                  <button className="grp-formula-token grp-formula-token--delete" onClick={() => appendFormula('⌫')}>
                    <i className="fa fa-long-arrow-left" />
                  </button>
                </div>
                <div className="grp-formula-tokens" style={{ marginTop: 6 }}>
                  {FORMULA_NUMBERS.map(n => (
                    <button key={n} className="grp-formula-token grp-formula-token--num" onClick={() => appendFormula(n)}>
                      {n}
                    </button>
                  ))}
                  <button className="grp-formula-token grp-formula-token--num" onClick={() => appendFormula('.')}>.</button>
                </div>

                {/* Live preview */}
                {formula.trim() && (
                  <p className="grp-formula-preview">
                    Preview (W=5, L=3): <strong>
                      {previewFormula() !== null ? previewFormula() : <span style={{ color: 'var(--red)' }}>invalid</span>}
                    </strong>
                  </p>
                )}

                {formulaMsg && <p className="grp-feedback grp-feedback--ok">{formulaMsg}</p>}
                {formulaErr && <p className="grp-feedback grp-feedback--err">{formulaErr}</p>}

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <button className="grp-btn grp-btn--primary" onClick={handleSaveFormula} disabled={formulaSaving}>
                    {formulaSaving ? <i className="fa fa-spinner fa-spin" /> : <><i className="fa fa-check" /> Save Formula</>}
                  </button>
                  {formula && (
                    <button className="grp-btn grp-btn--outline" onClick={() => { setFormula(''); }}>
                      <i className="fa fa-times" /> Clear
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Danger zone */}
            {isOwner && (
              <div className="grp-section grp-danger-zone">
                <h3 className="grp-section-label grp-section-label--danger">
                  <i className="fa fa-exclamation-triangle" /> Danger Zone
                </h3>
                {!confirmDel ? (
                  <button className="grp-btn grp-btn--danger" onClick={() => setConfirmDel(true)}>
                    <i className="fa fa-trash" /> Delete Group
                  </button>
                ) : (
                  <div className="grp-confirm-delete">
                    <p>Are you sure? This will permanently delete <strong>{group.name}</strong> and all its data.</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="grp-btn grp-btn--danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <i className="fa fa-spinner fa-spin" /> : 'Yes, Delete'}
                      </button>
                      <button className="grp-btn grp-btn--outline" onClick={() => setConfirmDel(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

      </div>
    </BaseLayout>
  );
}
